/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import {
	DESIGN_CONTEXT_READ_MODE,
	DesignContext,
	isSchemaConstObjectModel,
	serializeIDTToYAML,
	TBlueprintIDTNode,
	TGetBlueprintSchemaModel
} from "@adapptio/lib-blueprint";
import {
	createEventEmitter,
	emitEvent,
	offEvent,
	onEvent,
	removeAllEventListeners,
	TGetSimpleEventListener,
	UndoRedoManager
} from "@adapptio/lib-shared";
import { BlueprintReadme, TBlueprintReadmeSchema } from "@adapptio/lib-core";
import { ReadmeEditSelection } from "./ReadmeEditSelection";
import { Debounce } from "../../../decorators";

export class ReadmeModel {
	private readonly dCtx: DesignContext;

	public readonly onModelChange = createEventEmitter<void>();

	public readonly undoRedoManager = new UndoRedoManager({ historyLimit: 10 });

	private readonly selection = new ReadmeEditSelection();

	private model: TGetBlueprintSchemaModel<TBlueprintReadmeSchema> = null;

	private lastSnapshot: TBlueprintIDTNode | null = null;

	constructor() {
		this.dCtx = new DesignContext({
			resolvers: {},
			readMode: DESIGN_CONTEXT_READ_MODE.FULL,
			enableCompletition: true
		});

		onEvent(this.dCtx.modelChangeEvent, this.handleModelChange);
	}

	public dispose(): void {
		removeAllEventListeners(this.onModelChange);

		offEvent(this.dCtx?.modelChangeEvent, this.handleModelChange);

		if (this.model) {
			this.model.schema.destroy(this.model);
		}

		this.dCtx.destroy();
	}

	private handleModelChange: TGetSimpleEventListener<DesignContext["modelChangeEvent"]> = () => {
		this.checkSelection();
		this.createSnapshot();
		emitEvent(this.onModelChange);
	};

	public async createDefault(): Promise<void> {
		this.model = BlueprintReadme.createDefault(this.dCtx, null);

		await this.dCtx.waitForPendingOperations();
		await this.initModel();
	}

	private async initModel(): Promise<void> {
		this.handleModelChange();
	}

	/**
	 * Loads document
	 *
	 * @param blueprintContent Blueprint YAML contents
	 */
	public async loadDocument(blueprintContent: string): Promise<void> {
		this.model = this.dCtx.loadYAMLDocument(BlueprintReadme, blueprintContent, "local://readme");

		await this.dCtx.waitForPendingOperations();
		await this.initModel();
	}

	private serializeModelToIdt(): TBlueprintIDTNode {
		return this.model.schema.serialize(this.model, [ "$" ]);
	}

	/**
	 * Returns document as YAML
	 */
	public exportDocumentAsYAML(): string {
		if (!this.model) {
			throw new Error("Model is not initialized.");
		}

		const idt = this.serializeModelToIdt();

		return serializeIDTToYAML(idt);
	}

	private applySnapshotToModel(snapshot: TBlueprintIDTNode): void {
		this.model = BlueprintReadme.parse(this.dCtx, snapshot);
		this.initModel();
		this.lastSnapshot = snapshot;
		this.selection.deselectAll();
	}

	@Debounce(500)
	private createSnapshot(): void {
		const prevSnapshot = this.lastSnapshot;
		const currentSnapshot = this.serializeModelToIdt();
		this.lastSnapshot = currentSnapshot;

		if (prevSnapshot) {
			this.undoRedoManager.add({
				undo: () => this.applySnapshotToModel(prevSnapshot),
				redo: () => this.applySnapshotToModel(currentSnapshot),
				disposeUndo: () => {
					/** */
				},
				disposeRedo: () => {
					/** */
				}
			});

			emitEvent(this.onModelChange);
		}
	}

	private checkSelection(): void {
		const items = this.selection.getItems().slice();

		for (let i = 0; i < items.length; i++) {
			if (!this.dCtx.getNodeById(items[i].nodeId)) {
				this.selection.deselectByNodeId(items[i].nodeId);
			}
		}
	}

	public getDesignContext(): DesignContext {
		return this.dCtx;
	}

	public getModel(): TGetBlueprintSchemaModel<TBlueprintReadmeSchema> {
		return this.model;
	}

	public getSelection(): ReadmeEditSelection {
		return this.selection;
	}

	/**
	 * Select the first page if available
	 */
	public selectFirstPage(): void {
		const pagesModel = this.model?.props?.spec?.props?.pages;

		if (pagesModel && pagesModel.items.length > 0) {
			const firstPage = pagesModel.items[0];

			if (isSchemaConstObjectModel(firstPage)) {
				this.selection.selectPage(pagesModel, firstPage);
			}
		}
	}
}
